import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CallLogModal = ({ lead, onClose, onCallLogged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [callData, setCallData] = useState({
    call_duration: 0,
    outcome: '',
    notes: '',
    follow_up_date: '',
    is_valid_lead: null
  });

  const handleInputChange = (field, value) => {
    setCallData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!callData.outcome) {
      setError('Please select call outcome');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare call log data
      const logData = {
        call_date: new Date().toISOString(),
        call_duration: parseInt(callData.call_duration) || 0,
        outcome: callData.outcome,
        notes: callData.notes.trim(),
        follow_up_date: callData.follow_up_date || null,
        is_valid_lead: callData.is_valid_lead
      };

      // Prepare lead update if validation was performed
      let leadUpdate = null;
      if (callData.is_valid_lead === true) {
        leadUpdate = { status: 'validated' };
      } else if (callData.is_valid_lead === false) {
        leadUpdate = { status: 'rejected' };
      }

      // Call the parent handler
      await onCallLogged(logData, leadUpdate);

    } catch (error) {
      setError('Failed to log call');
      console.log('Call logging error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    // Convert MM:SS format to total seconds
    if (value.includes(':')) {
      const [minutes, seconds] = value.split(':');
      const totalSeconds = (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
      handleInputChange('call_duration', totalSeconds);
    } else {
      handleInputChange('call_duration', parseInt(value) || 0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Phone" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Log Call</h2>
              <p className="text-sm text-muted-foreground">{lead?.name} - {lead?.phone}</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Lead Information Preview */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {lead?.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{lead?.name}</p>
                  <p className="text-sm text-muted-foreground">{lead?.phone}</p>
                </div>
              </div>
              
              {(() => {
                // Parse skills - handle both JSON string and array
                let skills = [];
                if (lead?.skills) {
                  if (Array.isArray(lead.skills)) {
                    skills = lead.skills;
                  } else if (typeof lead.skills === 'string') {
                    try {
                      const parsed = JSON.parse(lead.skills);
                      skills = Array.isArray(parsed) ? parsed : [];
                    } catch {
                      // If not valid JSON, try splitting by comma
                      skills = lead.skills.split(',').map(s => s.trim()).filter(Boolean);
                    }
                  }
                }
                
                return skills.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {skills.slice(0, 4).map((skill, index) => (
                      <span 
                        key={`${lead?.id || 'lead'}-skill-${index}`}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary/10 text-primary"
                      >
                        {skill}
                      </span>
                    ))}
                    {skills.length > 4 && (
                      <span className="text-xs text-muted-foreground">+{skills.length - 4} more</span>
                    )}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Call Outcome */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Call Outcome *
                </label>
                <Select
                  value={callData.outcome}
                  onValueChange={(value) => handleInputChange('outcome', value)}
                  placeholder="Select outcome..."
                  required
                  options={[
                    { value: '', label: 'Select outcome...' },
                    { value: 'answered', label: 'Answered' },
                    { value: 'busy', label: 'Busy' },
                    { value: 'no_answer', label: 'No Answer' },
                    { value: 'invalid_number', label: 'Invalid Number' },
                    { value: 'callback_requested', label: 'Callback Requested' }
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Call Duration (MM:SS)
                </label>
                <Input
                  value={formatDuration(callData.call_duration)}
                  onChange={handleDurationChange}
                  placeholder="0:00"
                  pattern="[0-9]+:[0-5][0-9]"
                />
              </div>
            </div>

            {/* Lead Validation - Only show if call was answered */}
            {callData.outcome === 'answered' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Lead Validation
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    type="button"
                    variant={callData.is_valid_lead === true ? 'default' : 'outline'}
                    onClick={() => handleInputChange('is_valid_lead', true)}
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="CheckCircle" size={16} />
                    Valid Lead
                  </Button>
                  
                  <Button
                    type="button"
                    variant={callData.is_valid_lead === false ? 'destructive' : 'outline'}
                    onClick={() => handleInputChange('is_valid_lead', false)}
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="XCircle" size={16} />
                    Invalid Lead
                  </Button>
                  
                  <Button
                    type="button"
                    variant={callData.is_valid_lead === null ? 'secondary' : 'outline'}
                    onClick={() => handleInputChange('is_valid_lead', null)}
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="Clock" size={16} />
                    Need Follow-up
                  </Button>
                </div>
              </div>
            )}

            {/* Follow-up Date - Only show if callback requested or need follow-up */}
            {(callData.outcome === 'callback_requested' || 
              (callData.outcome === 'answered' && callData.is_valid_lead === null)) && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Follow-up Date
                </label>
                <Input
                  type="datetime-local"
                  value={callData.follow_up_date}
                  onChange={(e) => handleInputChange('follow_up_date', e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            )}

            {/* Call Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Call Notes
              </label>
              <textarea
                value={callData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add detailed notes about the conversation, candidate's interest, availability, etc."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
              />
            </div>

            {/* Quick Action Buttons */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium text-foreground mb-3">Quick Actions</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleInputChange('outcome', 'answered');
                    handleInputChange('is_valid_lead', true);
                    handleInputChange('notes', 'Candidate is interested and available for placement');
                  }}
                  className="text-xs"
                >
                  ✅ Interested
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleInputChange('outcome', 'answered');
                    handleInputChange('is_valid_lead', false);
                    handleInputChange('notes', 'Candidate is not interested at this time');
                  }}
                  className="text-xs"
                >
                  ❌ Not Interested
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleInputChange('outcome', 'callback_requested');
                    handleInputChange('notes', 'Candidate requested callback for detailed discussion');
                  }}
                  className="text-xs"
                >
                  📞 Callback
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleInputChange('outcome', 'busy');
                    handleInputChange('notes', 'Line was busy, will try again later');
                  }}
                  className="text-xs"
                >
                  📵 Busy
                </Button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !callData.outcome}
              className="flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Log Call
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CallLogModal;